import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { allShapes, readPresentation, shapesOfType } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionBildEinfuegen: Lesson = {
  id: "einfuegen",
  title: "Bilder einfügen",
  summary: "Über den Platzhalter statt über die Folienmitte – und mit genug Auflösung.",
  durationMinutes: 9,
  learningGoals: [
    "Bilder über den Inhaltsplatzhalter einfügen",
    "Die nötige Auflösung für Beamer und Druck einschätzen",
    "Erkennen, warum ein Bild im Platzhalter besser aufgehoben ist",
  ],
  steps: [
    {
      id: "ueber-den-platzhalter",
      title: "Der Weg über den Platzhalter",
      explanation: [
        "Ein Bild lässt sich auf zwei Wegen einfügen. Über „Einfügen“ → „Bilder“ landet " +
          "es in der Mitte der Folie und muss von Hand positioniert werden. Über das " +
          "Bildsymbol im leeren Inhaltsplatzhalter landet es genau in dem Bereich, den " +
          "das Layout dafür vorgesehen hat.",
        "Der Unterschied ist derselbe wie bei Text. Ein Bild im Platzhalter gehört zum " +
          "Layout: Es sitzt auf allen Folien an derselben Stelle, es rückt mit, wenn das " +
          "Layout sich ändert, und es lässt sich über einen Layoutwechsel neu anordnen. " +
          "Ein frei platziertes Bild liegt an absoluten Koordinaten und bleibt dort.",
        "Das gilt auch dann, wenn du das Bild anschließend doch verschiebst oder " +
          "vergrößerst. Solange es im Platzhalter steckt, bringt „Start“ → " +
          "„Zurücksetzen“ es wieder in Position.",
      ],
      keyPoints: [
        "Im leeren Inhaltsplatzhalter auf das Bildsymbol klicken.",
        "Layouts wie „Bild mit Überschrift“ bringen einen eigenen Bildplatzhalter mit.",
        "Ein Bild im Platzhalter lässt sich zurücksetzen, ein freies nicht.",
      ],
      task: {
        instruction: "Füge auf einer deiner Folien ein Bild ein.",
        hint: "Am besten über das Bildsymbol im leeren Inhaltsplatzhalter.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form (Bild, Form, Platzhalter)",
              "1.4",
              "Sieh selbst nach, ob auf einer Folie ein Bild liegt.",
            );
          }

          const bilder = shapesOfType(snapshot, "Image");
          const imPlatzhalter = allShapes(snapshot).filter(
            (form) => form.placeholder?.containedType === "Image",
          );
          const anzahl = bilder.length + imPlatzhalter.length;

          if (anzahl === 0) {
            return fail(
              "In der Präsentation ist noch kein Bild eingefügt.",
              "Auf einer Folie mit dem Layout „Titel und Inhalt“ auf das Bildsymbol im leeren Platzhalter klicken.",
            );
          }
          const erstes = bilder[0] ?? imPlatzhalter[0];
          return pass(`Bild gefunden – auf Folie ${erstes.slideIndex + 1}.`);
        },
      },
    },
    {
      id: "im-platzhalter",
      title: "Im Platzhalter statt daneben",
      explanation: [
        "Ob ein Bild wirklich im Platzhalter sitzt, verrät der Auswahlbereich: Ein Bild " +
          "im Platzhalter heißt dort weiterhin „Inhaltsplatzhalter 2“ oder " +
          "„Bildplatzhalter 3“. Ein frei eingefügtes Bild heißt „Bild 4“ oder " +
          "„Grafik 4“.",
        "Wenn ein Bild bereits frei auf der Folie liegt, ist der Weg zurück einfach: Bild " +
          "ausschneiden, in den leeren Platzhalter klicken, einfügen. PowerPoint setzt es " +
          "dann in den Rahmen und passt die Größe an.",
        "Für Folien, auf denen das Bild die Hauptrolle spielt, gibt es eigene Layouts. " +
          "„Bild mit Überschrift“ bringt einen großen Bildplatzhalter mit Titel und einer " +
          "kurzen Beschreibung darunter – genau das, was man sonst mühsam von Hand baut.",
      ],
      keyPoints: [
        "Auswahlbereich: Bild im Platzhalter behält den Platzhalternamen.",
        "Ausschneiden und in den leeren Platzhalter einfügen holt ein freies Bild zurück.",
        "Das Layout „Bild mit Überschrift“ ist für Bildfolien gedacht.",
      ],
      task: {
        instruction: "Sorge dafür, dass mindestens ein Bild in einem Platzhalter sitzt.",
        hint: "Bild ausschneiden (Strg + X), in den leeren Inhaltsplatzhalter klicken, einfügen (Strg + V).",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.placeholders) {
            return nichtPruefbar(
              "Der Inhalt eines Platzhalters",
              "1.8",
              "Öffne den Auswahlbereich: Heißt der Eintrag „Inhaltsplatzhalter“, sitzt das Bild richtig; heißt er „Bild“, liegt es frei.",
            );
          }

          const imPlatzhalter = allShapes(snapshot).filter(
            (form) => form.placeholder?.containedType === "Image",
          );
          const frei = shapesOfType(snapshot, "Image");

          if (imPlatzhalter.length > 0) {
            return pass(
              `Auf Folie ${imPlatzhalter[0].slideIndex + 1} steckt das Bild im Platzhalter – es rückt bei einem Layoutwechsel mit.` +
                (frei.length > 0
                  ? ` Daneben liegen noch ${frei.length} frei platzierte Bilder; das ist in Ordnung, solange sie bewusst dort sind.`
                  : ""),
            );
          }

          if (frei.length === 0) {
            return fail(
              "Es ist noch kein Bild eingefügt.",
              "Füge zuerst ein Bild ein, am besten über das Bildsymbol im Inhaltsplatzhalter.",
            );
          }

          return fail(
            `${frei.length === 1 ? "Das Bild liegt" : `Alle ${frei.length} Bilder liegen`} frei auf der Folie, ` +
              `zum Beispiel auf Folie ${frei[0].slideIndex + 1}.`,
            "Bild markieren, Strg + X, dann in den leeren Inhaltsplatzhalter klicken und Strg + V drücken.",
          );
        },
      },
    },
    {
      id: "aufloesung",
      title: "Genug Auflösung mitbringen",
      explanation: [
        "Ein Bild, das im Web gut aussieht, kann auf dem Beamer unscharf sein. Als " +
          "Faustregel gilt: Ein Bild, das eine ganze Folie füllen soll, braucht mindestens " +
          "1920 Pixel Breite. Ein Bild, das ein Viertel der Folie einnimmt, entsprechend " +
          "weniger.",
        "Umgekehrt schadet zu viel Auflösung auch. Eine Präsentation mit zwanzig " +
          "Fotos aus einer modernen Kamera wiegt schnell 200 MB und lässt sich weder " +
          "per Mail verschicken noch flüssig vorführen. Dafür gibt es die " +
          "Bildkomprimierung: Bild markieren → „Bildformat“ → „Bilder komprimieren“, " +
          "dort „Alle Bilder im Dokument“ und eine Auflösung wählen. 150 ppi reichen für " +
          "Bildschirm und Beamer.",
        "Ein Detail lohnt sich dabei: Das Häkchen „Zugeschnittene Bildbereiche löschen“ " +
          "entfernt die abgeschnittenen Teile endgültig. Ohne dieses Häkchen bleiben sie " +
          "in der Datei – und lassen sich von jedem wieder sichtbar machen, der die Datei " +
          "bekommt. Bei vertraulichen Inhalten ist das ein echtes Risiko.",
      ],
      keyPoints: [
        "Vollbild-Bilder: mindestens 1920 Pixel Breite.",
        "Bildformat → Bilder komprimieren, 150 ppi für Bildschirm und Beamer.",
        "„Zugeschnittene Bildbereiche löschen“ anhaken – sonst bleibt der Anschnitt in der Datei.",
      ],
    },
  ],
};
